import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import api from '../../services/api';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * SupervisorTimesOfService Component
 *
 * Allows Clinical Supervisors to manage Times of Service settings for their assigned service groups.
 */
function SupervisorTimesOfService({ token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [platformConfig, setPlatformConfig] = useState(null);
  const [groupConfigs, setGroupConfigs] = useState({});
  const [savingGroup, setSavingGroup] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch platform config (using supervisor-access endpoint) and supervisor's service groups
      const [platformRes, groupsRes] = await Promise.all([
        api.get('/admin/times-of-service/supervisor-access'),
        api.get('/service-groups/my-groups?role=supervisor')
      ]);

      if (platformRes.data.success) {
        setPlatformConfig(platformRes.data.config);
      }

      // Handle response format: { success: true, count: N, data: [...] }
      const groups = groupsRes.data.data || groupsRes.data.groups || groupsRes.data || [];
      setServiceGroups(Array.isArray(groups) ? groups : []);

      // Fetch configs for each service group
      const configs = {};
      for (const group of groups) {
        try {
          const configRes = await api.get(`/admin/times-of-service/service-group/${group._id}`);
          if (configRes.data.success) {
            configs[group._id] = configRes.data.config || getDefaultConfig();
          }
        } catch (err) {
          console.error(`Error fetching config for group ${group._id}:`, err);
          configs[group._id] = getDefaultConfig();
        }
      }
      setGroupConfigs(configs);
    } catch (err) {
      console.error('Error loading Times of Service data:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to edit service group schedules. This feature must be enabled by an administrator.');
      } else {
        setError('Failed to load service group data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getDefaultConfig = () => ({
    overrideEnabled: false,
    weeklySchedule: null,
    gracePeriod: {},
    outOfHoursExperience: {
      mode: 'platform_default',
      additionalPromptContext: '',
      groupEmergencyContacts: []
    }
  });

  const updateGroupConfig = (groupId, field, value) => {
    setGroupConfigs(prev => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        [field]: value
      }
    }));
  };

  const updateSchedule = (groupId, dayIndex, field, value) => {
    const config = groupConfigs[groupId] || getDefaultConfig();
    const currentSchedule = config.weeklySchedule ||
      platformConfig?.weeklySchedule?.map(s => ({ ...s })) ||
      DAYS_OF_WEEK.map((_, i) => ({ dayOfWeek: i, isOpen: false, periods: [] }));

    const newSchedule = [...currentSchedule];
    if (!newSchedule[dayIndex]) {
      newSchedule[dayIndex] = { dayOfWeek: dayIndex, isOpen: false, periods: [] };
    }

    if (field === 'isOpen') {
      newSchedule[dayIndex] = { ...newSchedule[dayIndex], isOpen: value };
      if (value && (!newSchedule[dayIndex].periods || newSchedule[dayIndex].periods.length === 0)) {
        newSchedule[dayIndex].periods = [{ startTime: '09:00', endTime: '17:00' }];
      }
    } else if (field === 'startTime' || field === 'endTime') {
      newSchedule[dayIndex] = {
        ...newSchedule[dayIndex],
        periods: [{ ...newSchedule[dayIndex].periods?.[0], [field]: value }]
      };
    }

    updateGroupConfig(groupId, 'weeklySchedule', newSchedule);
  };

  const applyPreset = (groupId, preset) => {
    let newSchedule;
    switch (preset) {
      case '24/7':
        newSchedule = DAYS_OF_WEEK.map((_, index) => ({
          dayOfWeek: index,
          isOpen: true,
          periods: [{ startTime: '00:00', endTime: '23:59' }]
        }));
        break;
      case 'business':
        newSchedule = DAYS_OF_WEEK.map((_, index) => ({
          dayOfWeek: index,
          isOpen: index >= 1 && index <= 5,
          periods: index >= 1 && index <= 5 ? [{ startTime: '09:00', endTime: '17:00' }] : []
        }));
        break;
      case 'copy_platform':
        newSchedule = platformConfig?.weeklySchedule?.map(s => ({ ...s })) || null;
        break;
      default:
        return;
    }
    updateGroupConfig(groupId, 'weeklySchedule', newSchedule);
  };

  const addEmergencyContact = (groupId) => {
    const config = groupConfigs[groupId] || getDefaultConfig();
    const contacts = config.outOfHoursExperience?.groupEmergencyContacts || [];
    updateGroupConfig(groupId, 'outOfHoursExperience', {
      ...config.outOfHoursExperience,
      groupEmergencyContacts: [...contacts, { name: '', phone: '', description: '' }]
    });
  };

  const updateEmergencyContact = (groupId, index, field, value) => {
    const config = groupConfigs[groupId] || getDefaultConfig();
    const contacts = [...(config.outOfHoursExperience?.groupEmergencyContacts || [])];
    contacts[index] = { ...contacts[index], [field]: value };
    updateGroupConfig(groupId, 'outOfHoursExperience', {
      ...config.outOfHoursExperience,
      groupEmergencyContacts: contacts
    });
  };

  const removeEmergencyContact = (groupId, index) => {
    const config = groupConfigs[groupId] || getDefaultConfig();
    const contacts = [...(config.outOfHoursExperience?.groupEmergencyContacts || [])];
    contacts.splice(index, 1);
    updateGroupConfig(groupId, 'outOfHoursExperience', {
      ...config.outOfHoursExperience,
      groupEmergencyContacts: contacts
    });
  };

  const saveGroupConfig = async (groupId) => {
    try {
      setSavingGroup(groupId);
      setError(null);

      const config = groupConfigs[groupId];
      const response = await api.put(
        `/admin/times-of-service/service-group/${groupId}`,
        config
      );

      if (response.data.success) {
        setSuccess(`Configuration saved for ${serviceGroups.find(g => g._id === groupId)?.name}`);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Error saving group config:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to edit this service group\'s schedule.');
      } else {
        setError('Failed to save configuration. Please try again.');
      }
    } finally {
      setSavingGroup(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Check if feature is enabled at platform level
  if (!platformConfig?.enabled) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/clinical-supervisor')} sx={{ mb: 2 }}>
          Back to Dashboard
        </Button>
        <Alert severity="info">
          Times of Service is not enabled at the platform level. Please contact an administrator.
        </Alert>
      </Box>
    );
  }

  // Check if supervisor editing is allowed
  if (!platformConfig?.allowClinicalSupervisorEdit) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/clinical-supervisor')} sx={{ mb: 2 }}>
          Back to Dashboard
        </Button>
        <Alert severity="warning">
          Clinical Supervisor editing of service group schedules is not enabled. Please contact an administrator to enable this feature.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/clinical-supervisor')}>
            Back
          </Button>
          <Typography variant="h5" display="flex" alignItems="center" gap={1}>
            <ScheduleIcon color="primary" />
            Service Hours Management
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadData}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Service Groups */}
      {serviceGroups.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <GroupsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No Service Groups Assigned
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You don't have supervisor access to any service groups with Times of Service configuration.
          </Typography>
        </Paper>
      ) : (
        serviceGroups.map((group) => {
          const config = groupConfigs[group._id] || getDefaultConfig();
          const isExpanded = expandedGroup === group._id;

          return (
            <Accordion
              key={group._id}
              expanded={isExpanded}
              onChange={() => setExpandedGroup(isExpanded ? null : group._id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <GroupsIcon color="primary" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1">{group.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {config.overrideEnabled ? 'Custom schedule enabled' : 'Using platform defaults'}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                {/* Override Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.overrideEnabled || false}
                      onChange={(e) => updateGroupConfig(group._id, 'overrideEnabled', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2">Enable Schedule Override</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Use a custom schedule for this service group instead of platform defaults
                      </Typography>
                    </Box>
                  }
                />

                {config.overrideEnabled && (
                  <>
                    <Divider sx={{ my: 3 }} />

                    {/* Quick Presets */}
                    <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Quick Presets</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button variant="outlined" size="small" onClick={() => applyPreset(group._id, '24/7')}>
                          24/7 Access
                        </Button>
                        <Button variant="outlined" size="small" onClick={() => applyPreset(group._id, 'business')}>
                          Business Hours
                        </Button>
                        <Button variant="outlined" size="small" onClick={() => applyPreset(group._id, 'copy_platform')}>
                          Copy Platform Schedule
                        </Button>
                      </Box>
                    </Box>

                    {/* Weekly Schedule */}
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>Weekly Schedule (UTC)</Typography>
                    {DAYS_OF_WEEK.map((day, dayIndex) => {
                      const schedule = config.weeklySchedule || platformConfig?.weeklySchedule || [];
                      const daySchedule = schedule.find(s => s.dayOfWeek === dayIndex) || { isOpen: false, periods: [] };

                      return (
                        <Box key={dayIndex} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={daySchedule.isOpen}
                                onChange={(e) => updateSchedule(group._id, dayIndex, 'isOpen', e.target.checked)}
                              />
                            }
                            label={<Typography sx={{ width: 100 }}>{day}</Typography>}
                          />
                          {daySchedule.isOpen ? (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                type="time"
                                size="small"
                                value={daySchedule.periods?.[0]?.startTime || '09:00'}
                                onChange={(e) => updateSchedule(group._id, dayIndex, 'startTime', e.target.value)}
                                sx={{ width: 120 }}
                              />
                              <Typography>to</Typography>
                              <TextField
                                type="time"
                                size="small"
                                value={daySchedule.periods?.[0]?.endTime || '17:00'}
                                onChange={(e) => updateSchedule(group._id, dayIndex, 'endTime', e.target.value)}
                                sx={{ width: 120 }}
                              />
                            </Box>
                          ) : (
                            <Typography color="text.secondary">Closed</Typography>
                          )}
                        </Box>
                      );
                    })}

                    <Divider sx={{ my: 3 }} />

                    {/* Out-of-Hours Experience */}
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>Out-of-Hours Experience</Typography>

                    <FormControl component="fieldset" sx={{ mb: 2 }}>
                      <FormLabel component="legend">Mode</FormLabel>
                      <RadioGroup
                        value={config.outOfHoursExperience?.mode || 'platform_default'}
                        onChange={(e) => updateGroupConfig(group._id, 'outOfHoursExperience', {
                          ...config.outOfHoursExperience,
                          mode: e.target.value
                        })}
                      >
                        <FormControlLabel value="platform_default" control={<Radio />} label="Use Platform Default" />
                        <FormControlLabel value="helper_persona" control={<Radio />} label="Helper Persona (Limited Support)" />
                        <FormControlLabel value="closed_screen" control={<Radio />} label="Closed Screen (No Chat)" />
                      </RadioGroup>
                    </FormControl>

                    {/* Additional Prompt Context */}
                    <TextField
                      label="Additional Context for Helper Persona"
                      fullWidth
                      multiline
                      rows={3}
                      value={config.outOfHoursExperience?.additionalPromptContext || ''}
                      onChange={(e) => updateGroupConfig(group._id, 'outOfHoursExperience', {
                        ...config.outOfHoursExperience,
                        additionalPromptContext: e.target.value
                      })}
                      placeholder="Add group-specific context, contact details, or instructions for the helper persona..."
                      helperText="This context will be injected into the helper persona prompt for this service group"
                      sx={{ mb: 3 }}
                    />

                    {/* Group Emergency Contacts */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">Group-Specific Emergency Contacts</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => addEmergencyContact(group._id)}>
                          Add Contact
                        </Button>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        These contacts will be shown in addition to platform-level emergency contacts
                      </Typography>

                      {(config.outOfHoursExperience?.groupEmergencyContacts || []).map((contact, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                          <TextField
                            size="small"
                            label="Name"
                            value={contact.name}
                            onChange={(e) => updateEmergencyContact(group._id, index, 'name', e.target.value)}
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            size="small"
                            label="Phone"
                            value={contact.phone}
                            onChange={(e) => updateEmergencyContact(group._id, index, 'phone', e.target.value)}
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            size="small"
                            label="Description"
                            value={contact.description || ''}
                            onChange={(e) => updateEmergencyContact(group._id, index, 'description', e.target.value)}
                            sx={{ flex: 1.5 }}
                          />
                          <IconButton size="small" onClick={() => removeEmergencyContact(group._id, index)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}

                {/* Save Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => saveGroupConfig(group._id)}
                    disabled={savingGroup === group._id}
                  >
                    {savingGroup === group._id ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </Box>
  );
}

export default SupervisorTimesOfService;
